import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let app = null;
let analytics = null;
let auth = null;
let db = null;

try {
  if (firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    if (firebaseConfig.measurementId) {
      analytics = getAnalytics(app);
    }
    auth = getAuth(app);
    db = getFirestore(app);
  }
} catch (error) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Firebase not configured:', error.message);
  }
}

export { auth, db };
export const googleProvider = new GoogleAuthProvider();

export const trackEvent = (eventName, eventData = {}) => {
  try {
    if (analytics) logEvent(analytics, eventName, eventData);
  } catch {
    // Analytics unavailable — silently ignore
  }
};

export const getAppConfig = async () => {
  if (!db) return null;
  try {
    const snap = await getDoc(doc(db, 'config', 'appConfig'));
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
};

export const saveQRCode = async (userId, qrData) => {
  if (!db || !userId) return null;
  try {
    const ref = collection(db, 'users', userId, 'qr_codes');
    const docRef = await addDoc(ref, {
      ...qrData,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch {
    return null;
  }
};

export const getUserQRCodes = async (userId, maxItems = 50) => {
  if (!db || !userId) return [];
  try {
    const ref = collection(db, 'users', userId, 'qr_codes');
    const q = query(ref, orderBy('createdAt', 'desc'), limit(maxItems));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch {
    return [];
  }
};

export const deleteQRCode = async (userId, docId) => {
  if (!db || !userId) return;
  try {
    await deleteDoc(doc(db, 'users', userId, 'qr_codes', docId));
  } catch {
    // silently fail — caller treats as success for optimistic UI
  }
};

export const getUserProfile = async (userId) => {
  if (!db || !userId) return null;
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    return snap.exists() ? snap.data() : null;
  } catch {
    return null;
  }
};

export const setUserProfile = async (userId, data) => {
  if (!db || !userId) return;
  try {
    await setDoc(doc(db, 'users', userId), data, { merge: true });
  } catch {
    // silently fail
  }
};

export default app;
